/**
 * Training Admin — admin surface for the training-compliance module. Three tabs:
 *   1. Catalogue    — courses fetched live from Dojo (slug + title + test URL)
 *   2. Requirements — who must take what, by when (scope + frequency + due date)
 *   3. Compliance   — per-course status table (pick a course first)
 *
 * Live data via the 64-04 API; the approved mockup design is the visual contract
 * (only the data source is swapped here).
 */
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';
import { Button } from '../../components/common/Button';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { TrainingEvidenceExportDialog } from '../../components/training/TrainingEvidenceExportDialog';
import {
  AcademicCapIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '../../components/ui/icons';
import {
  useAvailableCourses,
  useSyncCatalog,
  useTrainingRequirements,
  useCreateRequirement,
  useUpdateRequirement,
  useDeleteRequirement,
  useTrainingComplianceMatrix,
} from '../../hooks/useTrainings';
import { useAdminUsers, useEmployeeTypes } from '../../hooks/useAdmin';
import { UserPicker, userDisplayName } from '../../components/admin/UserPicker';
import type {
  AvailableCourseDTO,
  ComplianceMatrixRowDTO,
  ComplianceStatusFilter,
  RequirementCreateInput,
  RequirementDTO,
} from '../../services/api/training';
import { DateField } from '../../components/ui/DateField';

// Compliance state is mutually exclusive. "Due soon" is NOT a state — it's a
// separate timing flag that can ride alongside `compliant` (renewal nearing)
// or `not_started` (first deadline nearing). Overdue never carries it.
type ComplianceState = 'compliant' | 'not_started' | 'overdue';
type Participation = 'mandatory' | 'optional' | 'not_required';
type Frequency = 'oneoff' | 'annual';

// ── Small presentational helpers ───────────────────────────────────────────

/** Resolvers turning stored scopeRef ids into human-readable names. */
interface ScopeResolvers {
  userName: (id: string) => string;
  typeName: (id: string) => string;
}

function scopeLabel(r: RequirementDTO, resolve: ScopeResolvers): string {
  switch (r.scope) {
    case 'all':
      return 'All employees & contractors';
    case 'employee_type':
      return `Employee type: ${r.scopeRef ? resolve.typeName(r.scopeRef) : '—'}`;
    case 'role':
      return `Role: ${r.scopeRef ?? '—'}`;
    case 'user':
      return `User: ${r.scopeRef ? resolve.userName(r.scopeRef) : '—'}`;
    default:
      return r.scope;
  }
}

/** "except Anna N., Deploy Bot" suffix for a requirement's exclusion list. */
function exclusionsLabel(r: RequirementDTO, resolve: ScopeResolvers): string | null {
  if (!r.excludedUserIds?.length) return null;
  return `except ${r.excludedUserIds.map((id) => resolve.userName(String(id))).join(', ')}`;
}

function scheduleText(r: RequirementDTO): string {
  const freq = r.frequency === 'annual' ? 'Annual' : 'One-off';
  let due: string;
  if (r.anchor === 'fixed') {
    const label = r.fixedDueDate
      ? format(new Date(r.fixedDueDate), r.frequency === 'annual' ? 'd MMM' : 'd MMM yyyy', { locale: cs })
      : 'fixed date';
    due = `fixed date (${label})`;
  } else {
    due = `${r.graceDays ?? 30}-day grace period`;
  }
  return `${freq} · ${due}`;
}

// Server returns the raw nextDueAt; derive the same one-line note the mockup showed.
function matrixNote(row: ComplianceMatrixRowDTO): string | undefined {
  if (!row.nextDueAt) return undefined;
  const d = format(new Date(row.nextDueAt), 'd MMM yyyy', { locale: cs });
  if (row.state === 'overdue') return `was due ${d}`;
  if (row.state === 'compliant') return `${row.dueSoon ? 'renews' : 'expires'} ${d}`;
  return `due ${d}`;
}

function personName(user: ComplianceMatrixRowDTO['user']): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email;
}

const STATE_STYLE: Record<ComplianceState, { label: string; cls: string }> = {
  compliant: { label: 'Compliant', cls: 'bg-green-100 text-green-800' },
  not_started: { label: 'Not started', cls: 'bg-gray-100 text-gray-600' },
  overdue: { label: 'Overdue', cls: 'bg-red-100 text-red-800' },
};

function StatusPill({
  state,
  dueSoon,
  note,
}: {
  state: ComplianceState;
  dueSoon?: boolean;
  note?: string;
}) {
  const s = STATE_STYLE[state];
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium ${s.cls}`}>
          {s.label}
        </span>
        {dueSoon && (
          <span className="inline-flex w-fit rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
            Due soon
          </span>
        )}
      </div>
      {note && <span className="text-[11px] text-gray-400">{note}</span>}
    </div>
  );
}

const PARTICIPATION_STYLE: Record<Participation, { label: string; cls: string }> = {
  mandatory: { label: 'Mandatory', cls: 'bg-red-50 text-red-700' },
  optional: { label: 'Optional', cls: 'bg-blue-50 text-blue-700' },
  not_required: { label: 'Not required', cls: 'bg-gray-100 text-gray-400' },
};

function ParticipationBadge({ participation }: { participation: Participation }) {
  const p = PARTICIPATION_STYLE[participation];
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${p.cls}`}>
      {p.label}
    </span>
  );
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border-b-2 px-1 pb-2 text-sm font-medium transition-colors ${
        active
          ? 'border-primary-600 text-primary-700'
          : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

type TabKey = 'catalogue' | 'requirements' | 'compliance';

// People-search debounce. App conventions: @mention typeahead 150ms, fold-state
// write 250ms — 500ms is intentionally calmer for a DB-backed people search.
const SEARCH_DEBOUNCE_MS = 500;

export function TrainingAdminPage() {
  const [tab, setTab] = useState<TabKey>('catalogue');
  const [showNewReq, setShowNewReq] = useState(false);
  const [editingReq, setEditingReq] = useState<RequirementDTO | null>(null);
  const [matrixCourse, setMatrixCourse] = useState<string>(''); // '' = none picked
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Raw search input vs. the debounced term actually sent to the API.
  const [searchInput, setSearchInput] = useState('');
  const [matrixSearch, setMatrixSearch] = useState('');
  const [matrixStatus, setMatrixStatus] = useState<ComplianceStatusFilter>('all');

  // Debounce the people search so each keystroke doesn't hit the API. 500ms for
  // a DB-backed people search (heavier than the 150ms @mention typeahead). A new
  // term resets to page 1.
  useEffect(() => {
    const t = setTimeout(() => {
      setMatrixSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const [evidenceOpen, setEvidenceOpen] = useState(false);

  const coursesQuery = useAvailableCourses();
  const courses = coursesQuery.data?.courses ?? [];
  const lastSyncAt = coursesQuery.data?.lastSyncAt ?? null;
  const syncCatalogMut = useSyncCatalog();

  const requirementsQuery = useTrainingRequirements();
  const requirements = requirementsQuery.data ?? [];

  // Name lookups so requirement rows read "Employee type: Contractor" and
  // "User: Anna N." instead of raw ids. Unresolvable ids stay visible as #id.
  const adminUsersQuery = useAdminUsers();
  const employeeTypesQuery = useEmployeeTypes();
  const scopeResolvers: ScopeResolvers = {
    userName: (id) => {
      const u = (adminUsersQuery.data ?? []).find((x) => String(x.id) === id);
      return u ? userDisplayName(u) : `#${id}`;
    },
    typeName: (id) => {
      const t = (employeeTypesQuery.data ?? []).find((x) => String(x.id) === id);
      return t?.name ?? `#${id}`;
    },
  };

  // Server-side filtered + paginated — search and status are query params, not
  // a client-side soft filter.
  const matrixQuery = useTrainingComplianceMatrix(matrixCourse, page, pageSize, {
    search: matrixSearch || undefined,
    status: matrixStatus,
  });
  const matrixRows = matrixQuery.data?.data ?? [];
  const matrixPagination = matrixQuery.data?.pagination;

  // Only courses with an active requirement can appear in an evidence report.
  const evidenceCourses = Array.from(
    requirements
      .filter((r) => r.active)
      .reduce((map, r) => {
        const prev = map.get(r.courseSlug);
        map.set(r.courseSlug, {
          slug: r.courseSlug,
          title: prev?.title || r.courseTitle || r.courseSlug,
          optional: (prev?.optional ?? true) && !r.mandatory,
        });
        return map;
      }, new Map<string, { slug: string; title: string; optional: boolean }>())
      .values(),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AcademicCapIcon className="h-7 w-7 text-primary-600" />
          <h1 className="text-2xl font-semibold text-gray-900">Training</h1>
        </div>
        <Button variant="secondary" onClick={() => setEvidenceOpen(true)}>
          <DocumentArrowDownIcon className="mr-2 h-5 w-5" aria-hidden="true" />
          Export evidence (PDF)
        </Button>
      </div>
      <p className="-mt-3 max-w-2xl text-sm text-gray-500">
        Monksheets is the source of truth for required trainings, due dates and annual expiry.
        Courses come from Dojo; employees take the test there and the pass flows back here as
        evidence.
      </p>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-gray-200">
        <Tab active={tab === 'catalogue'} onClick={() => setTab('catalogue')}>
          Catalogue
        </Tab>
        <Tab active={tab === 'requirements'} onClick={() => setTab('requirements')}>
          Requirements
        </Tab>
        <Tab active={tab === 'compliance'} onClick={() => setTab('compliance')}>
          Compliance
        </Tab>
      </div>

      {/* ── Catalogue ── */}
      {tab === 'catalogue' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {syncCatalogMut.isError
                ? 'Could not queue the sync — please try again.'
                : syncCatalogMut.isSuccess
                  ? 'Sync queued — the catalogue will update shortly.'
                  : lastSyncAt
                    ? `Last synced ${format(new Date(lastSyncAt), 'd MMM yyyy, HH:mm')}.`
                    : 'Not synced yet — click “Sync from Dojo” to populate the catalogue.'}
            </p>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => syncCatalogMut.mutate()}
              isLoading={syncCatalogMut.isPending}
            >
              Sync from Dojo
            </Button>
          </div>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-4 py-3">Course</th>
                  <th className="px-4 py-3">Slug</th>
                  <th className="px-4 py-3">Test link</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {courses.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-400">
                      No courses yet — click “Sync from Dojo”.
                    </td>
                  </tr>
                ) : (
                  courses.map((c) => (
                    <tr key={c.slug} className={c.active ? '' : 'opacity-60'}>
                      <td className="px-4 py-3 font-medium text-gray-900">{c.title}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">{c.slug}</td>
                      <td className="px-4 py-3">
                        <a
                          href={c.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary-600 hover:underline"
                        >
                          /play/{c.slug}
                          <ArrowRightIcon className="h-3.5 w-3.5" />
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        {c.active ? (
                          <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                            Active
                          </span>
                        ) : (
                          <span
                            className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
                            title={`No longer offered by Dojo as of the last sync (last seen ${format(new Date(c.lastSeenAt), 'd MMM yyyy')})`}
                          >
                            Inactive
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400">
            Course descriptions will come from Dojo later (single source of truth). For now the
            catalogue is name + test link.
          </p>
        </div>
      )}

      {/* ── Requirements ── */}
      {tab === 'requirements' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Rules that decide who must take a course and by when. Most-specific rule wins
              (user &gt; group &gt; all).
            </p>
            {!showNewReq && !editingReq && (
              <Button size="sm" onClick={() => setShowNewReq(true)}>
                <PlusIcon className="mr-1 h-4 w-4" />
                New requirement
              </Button>
            )}
          </div>

          {(showNewReq || editingReq) && (
            <NewRequirementForm
              key={editingReq ? `edit-${editingReq.id}` : 'new'}
              courses={courses}
              editing={editingReq}
              onClose={() => {
                setShowNewReq(false);
                setEditingReq(null);
              }}
            />
          )}

          <div className="space-y-2">
            {requirements.map((r) => (
              <div
                key={r.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{r.courseTitle}</span>
                    <ParticipationBadge participation={r.mandatory ? 'mandatory' : 'optional'} />
                  </div>
                  <div className="mt-1 text-sm text-gray-500">
                    {scopeLabel(r, scopeResolvers)}
                    {exclusionsLabel(r, scopeResolvers) && (
                      <span className="text-amber-700"> — {exclusionsLabel(r, scopeResolvers)}</span>
                    )}{' '}
                    · {scheduleText(r)} · reminder T-14 + overdue
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingReq(r);
                      setShowNewReq(false);
                    }}
                  >
                    Edit
                  </Button>
                  <DeleteRequirementButton id={r.id} title={r.courseTitle} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Compliance (per-course) ── */}
      {tab === 'compliance' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              Course
              <select
                value={matrixCourse}
                onChange={(e) => {
                  setMatrixCourse(e.target.value);
                  setPage(1);
                  setSearchInput('');
                  setMatrixSearch('');
                  setMatrixStatus('all');
                }}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">— Select a course —</option>
                {courses.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.title}
                  </option>
                ))}
              </select>
            </label>
            {matrixCourse && (
              <>
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search people…"
                    className="rounded-lg border border-gray-300 py-2 pl-8 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <select
                  value={matrixStatus}
                  onChange={(e) => {
                    setMatrixStatus(e.target.value as ComplianceStatusFilter);
                    setPage(1);
                  }}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 focus:outline-none"
                >
                  <option value="all">All statuses</option>
                  <option value="compliant">Compliant</option>
                  <option value="due_soon">Due soon</option>
                  <option value="not_started">Not started</option>
                  <option value="overdue">Overdue</option>
                  <option value="not_required">Not required</option>
                </select>
              </>
            )}
          </div>

          {!matrixCourse ? (
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-12 text-center text-sm text-gray-500">
              Pick a course above to see who must take it and where they stand.
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Person</th>
                    <th className="px-4 py-3">Requirement</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {matrixRows.map((row) => {
                    const dimmed = row.participation === 'not_required';
                    return (
                      <tr key={row.user.id} className={dimmed ? 'opacity-60' : ''}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{personName(row.user)}</div>
                          <div className="text-xs text-gray-400">{row.user.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <ParticipationBadge participation={row.participation} />
                        </td>
                        <td className="px-4 py-3">
                          {row.participation === 'not_required' ? (
                            <span className="text-gray-300">—</span>
                          ) : (
                            <StatusPill
                              state={(row.state ?? 'not_started') as ComplianceState}
                              dueSoon={row.dueSoon}
                              note={matrixNote(row)}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <PaginationFooter
                total={matrixPagination?.total ?? matrixRows.length}
                page={page}
                pageSize={pageSize}
                totalPages={matrixPagination?.totalPages ?? 1}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
              />
            </div>
          )}
        </div>
      )}

      <TrainingEvidenceExportDialog
        isOpen={evidenceOpen}
        onClose={() => setEvidenceOpen(false)}
        courses={evidenceCourses}
        employeeTypes={(employeeTypesQuery.data ?? []).map((t) => ({ id: t.id, name: t.name }))}
      />
    </div>
  );
}

// New-requirement config panel — the scope + frequency/due controls the admin
// uses; submits a real POST /api/admin/training/requirements on Create.
function NewRequirementForm({
  courses,
  editing,
  onClose,
}: {
  courses: AvailableCourseDTO[];
  editing?: RequirementDTO | null;
  onClose: () => void;
}) {
  const createMut = useCreateRequirement();
  const updateMut = useUpdateRequirement();
  const mut = editing ? updateMut : createMut;

  // When editing, seed the controls from the existing requirement (the parent
  // remounts this form via `key`, so these initial values apply cleanly).
  const [courseSlug, setCourseSlug] = useState<string>(
    editing?.courseSlug ?? courses[0]?.slug ?? '',
  );
  const [mandatory, setMandatory] = useState(editing?.mandatory ?? true);
  const [scope, setScope] = useState<'all' | 'type' | 'role' | 'user'>(
    editing ? (editing.scope === 'employee_type' ? 'type' : editing.scope) : 'all',
  );
  const [scopeRef, setScopeRef] = useState(editing?.scopeRef ?? '');
  const [excludedUserIds, setExcludedUserIds] = useState<number[]>(
    editing?.excludedUserIds ?? [],
  );
  const [frequency, setFrequency] = useState<Frequency>(editing?.frequency ?? 'annual');

  // Options for the scope selectors: employee types by name, roles as the
  // distinct set actually assigned to users (no dedicated roles endpoint).
  const employeeTypes = useEmployeeTypes().data ?? [];
  const adminUsers = useAdminUsers().data ?? [];
  const roleOptions = [...new Set(adminUsers.flatMap((u) => u.roles))].sort();
  const [dueKind, setDueKind] = useState<'fixed' | 'relative'>(editing?.anchor ?? 'fixed');
  const [fixedDate, setFixedDate] = useState<string>(
    editing?.fixedDueDate ? editing.fixedDueDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
  );
  const [graceDays, setGraceDays] = useState<number>(editing?.graceDays ?? 30);

  const field =
    'rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500';

  // Plain-language summary of the chosen frequency × due combination.
  const summary = (() => {
    if (frequency === 'annual' && dueKind === 'fixed')
      return 'Due every year on the fixed date — all applicable staff share the same deadline.';
    if (frequency === 'annual' && dueKind === 'relative')
      return 'Each person becomes compliant within the grace period (from joining, or assignment for existing staff), then renews every year.';
    if (frequency === 'oneoff' && dueKind === 'fixed')
      return 'Due once, on the fixed date. No renewal.';
    return 'Each person becomes compliant within the grace period (from joining, or assignment for existing staff). Lifetime — no renewal.';
  })();

  const apiScope: RequirementCreateInput['scope'] =
    scope === 'all' ? 'all' : scope === 'type' ? 'employee_type' : scope;
  const invalid =
    !courseSlug ||
    (scope !== 'all' && !scopeRef.trim()) ||
    (dueKind === 'fixed' && !fixedDate);

  function handleSubmit() {
    const payload: RequirementCreateInput = {
      courseSlug,
      courseTitle: courses.find((c) => c.slug === courseSlug)?.title ?? editing?.courseTitle ?? courseSlug,
      mandatory,
      active: editing?.active ?? true,
      scope: apiScope,
      scopeRef: apiScope === 'all' ? null : scopeRef.trim(),
      frequency,
      anchor: dueKind,
      fixedDueDate: dueKind === 'fixed' ? fixedDate : null,
      graceDays: dueKind === 'relative' ? graceDays : null,
      recurMonths: frequency === 'annual' ? 12 : null,
      excludedUserIds: apiScope === 'user' ? [] : excludedUserIds,
    };
    if (editing) {
      updateMut.mutate({ id: editing.id, input: payload }, { onSuccess: onClose });
    } else {
      createMut.mutate(payload, { onSuccess: onClose });
    }
  }

  return (
    <div className="space-y-4 rounded-lg border border-primary-200 bg-primary-50/40 p-4">
      <div className="grid gap-4 md:grid-cols-2">
        <label className="space-y-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Course</span>
          <select
            className={`${field} w-full`}
            value={courseSlug}
            onChange={(e) => setCourseSlug(e.target.value)}
          >
            {courses.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.title}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            Mandatory?
          </span>
          <select
            className={`${field} w-full`}
            value={mandatory ? 'Mandatory' : 'Optional'}
            onChange={(e) => setMandatory(e.target.value === 'Mandatory')}
          >
            <option>Mandatory</option>
            <option>Optional</option>
          </select>
        </label>
      </div>

      <div className="space-y-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          Who must take it
        </span>
        <div className="flex flex-wrap gap-2">
          {(
            [
              ['all', 'Everyone'],
              ['type', 'Employee type'],
              ['role', 'Role / group'],
              ['user', 'Single person'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                setScope(k);
                setScopeRef('');
              }}
              className={`rounded-full px-3 py-1 text-sm ${
                scope === k
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 ring-1 ring-gray-300 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {scope === 'type' && (
          <select
            value={scopeRef}
            onChange={(e) => setScopeRef(e.target.value)}
            className={`${field} mt-1 w-full md:w-1/2`}
          >
            <option value="">Select employee type…</option>
            {employeeTypes.map((t) => (
              <option key={t.id} value={String(t.id)}>
                {t.name ?? `#${t.id}`}
              </option>
            ))}
          </select>
        )}
        {scope === 'role' && (
          <select
            value={scopeRef}
            onChange={(e) => setScopeRef(e.target.value)}
            className={`${field} mt-1 w-full md:w-1/2`}
          >
            <option value="">Select role…</option>
            {roleOptions.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        )}
        {scope === 'user' && (
          <div className="mt-1 w-full md:w-1/2">
            <UserPicker
              single
              value={scopeRef && /^\d+$/.test(scopeRef) ? [Number(scopeRef)] : []}
              onChange={(ids) => setScopeRef(ids[0] != null ? String(ids[0]) : '')}
              placeholder="Search for a person…"
            />
          </div>
        )}
      </div>

      {/* Exclusions — everyone in scope EXCEPT these users (e.g. robotic
          accounts under an 'all' scope). Irrelevant for a single-person scope. */}
      {scope !== 'user' && (
        <div className="space-y-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            Excluded users
          </span>
          <div className="w-full md:w-1/2">
            <UserPicker
              value={excludedUserIds}
              onChange={setExcludedUserIds}
              placeholder="Search users to exclude…"
            />
          </div>
          <p className="text-xs text-gray-400">
            Excluded users are never required to take this training, even when the scope above
            matches them.
          </p>
        </div>
      )}

      {/* Two orthogonal axes — kept adjacent because both shape the input below. */}
      <div className="flex flex-wrap gap-x-10 gap-y-3">
        <div className="space-y-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            Frequency
          </span>
          <div className="flex gap-2">
            {(
              [
                ['annual', 'Annual'],
                ['oneoff', 'One-off'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setFrequency(k)}
                className={`rounded-full px-3 py-1 text-sm ${
                  frequency === k
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-600 ring-1 ring-gray-300 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            Due date
          </span>
          <div className="flex gap-2">
            {(
              [
                ['fixed', 'Fixed date'],
                ['relative', 'Grace period'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setDueKind(k)}
                className={`rounded-full px-3 py-1 text-sm ${
                  dueKind === k
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-600 ring-1 ring-gray-300 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Conditional input for the chosen due-date kind. Distinct `key`s force a
          remount when switching fixed ↔ grace, so the field type (date vs. number
          of days) and its default value reset correctly. */}
      <div className="text-sm text-gray-600">
        {dueKind === 'fixed' ? (
          <span key="fixed" className="inline-flex items-center gap-2">
            {frequency === 'annual' ? 'Due every year on' : 'Due once on'}
            <DateField
              key="fixed-date"
              value={fixedDate}
              onChange={setFixedDate}
              className={`${field} w-36`}
            />
          </span>
        ) : (
          <span key="grace" className="inline-flex items-center gap-2">
            Grace period
            <input
              key="grace-days"
              type="number"
              min={1}
              value={graceDays}
              onChange={(e) => setGraceDays(Number(e.target.value))}
              className={`${field} w-20`}
            />
            days to become compliant (from joining, or assignment for existing staff).
          </span>
        )}
      </div>

      <p className="rounded-md bg-white/70 px-3 py-2 text-xs text-gray-500 ring-1 ring-gray-200">
        {summary}
      </p>

      {mut.isError && (
        <p className="text-xs text-red-600">
          Could not {editing ? 'save' : 'create'} the requirement — please try again.
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <Button size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSubmit} disabled={invalid} isLoading={mut.isPending}>
          {editing ? 'Save changes' : 'Create requirement'}
        </Button>
      </div>
    </div>
  );
}

// Delete control for a requirement row — confirms, then removes via the API.
function DeleteRequirementButton({ id, title }: { id: number; title: string }) {
  const deleteMut = useDeleteRequirement();
  return (
    <Button
      size="sm"
      variant="ghost"
      isLoading={deleteMut.isPending}
      onClick={() => {
        if (window.confirm(`Delete the requirement for “${title}”? This cannot be undone.`)) {
          deleteMut.mutate(id);
        }
      }}
    >
      Delete
    </Button>
  );
}

// Table pagination footer. Default (uncontrolled) usage stays pixel-identical to
// the approved mockup — disabled prev/next, "1–{total} of {total}", static page
// size. When the controlled props are supplied it reflects server pagination.
export function PaginationFooter({
  total,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
}: {
  total: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}) {
  const controlled = page != null && pageSize != null && totalPages != null;
  const start = controlled ? (total === 0 ? 0 : (page - 1) * pageSize + 1) : 1;
  const end = controlled ? Math.min(page * pageSize, total) : total;
  const canPrev = controlled ? page > 1 : false;
  const canNext = controlled ? page < totalPages : false;

  const navBtn = (enabled: boolean) =>
    `rounded border border-gray-200 px-2 py-1 ${
      enabled ? 'text-gray-600 hover:bg-gray-50' : 'text-gray-300'
    }`;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3 text-sm text-gray-500">
      <label className="flex items-center gap-2">
        Rows per page:
        <select
          className="rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          {...(controlled
            ? { value: pageSize, onChange: (e) => onPageSizeChange?.(Number(e.target.value)) }
            : {})}
        >
          <option>10</option>
          <option>25</option>
          <option>50</option>
          <option>100</option>
        </select>
      </label>
      <div className="flex items-center gap-3">
        <span>
          {start}–{end} of {total}
        </span>
        <div className="flex gap-1">
          <button
            disabled={!canPrev}
            onClick={controlled && page != null ? () => onPageChange?.(page - 1) : undefined}
            className={navBtn(canPrev)}
            aria-label="Previous page"
          >
            ‹
          </button>
          <button
            disabled={!canNext}
            onClick={controlled && page != null ? () => onPageChange?.(page + 1) : undefined}
            className={navBtn(canNext)}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}
